import { useStyletron } from "baseui";
import { uniq } from "lodash";
import type { ReactNode } from "react";
import React from "react";
import type { ShopifyApiVersion } from "state-trees/src/connections/shopify/ShopifyApiVersion";
import type { ShopifyModelMetadata } from "state-trees/src/connections/shopify/ShopifyModelMetadata";
import {
  eachShopifyModelMetadataForVersion,
  shopifyModelMetadataForVersion,
  shopifyModelSpecOptions,
} from "state-trees/src/connections/shopify/ShopifyModelMetadata";
import {
  ShopifyAppCreditKey,
  ShopifyAppPurchaseOneTimeKey,
  ShopifyAppSubscriptionKey,
  ShopifyArticleKey,
  ShopifyAssetKey,
  ShopifyBlogKey,
  ShopifyCheckoutKey,
  ShopifyCollectionKey,
  ShopifyCompanyKey,
  ShopifyDisputeKey,
  ShopifyDomainKey,
  ShopifyFileKey,
  ShopifyGdprRequestKey,
  ShopifyGiftCardKey,
  ShopifyInventoryItemKey,
  ShopifyInventoryLevelKey,
  ShopifyPageKey,
  ShopifyProductImageKey,
  ShopifyShopKey,
  ShopifySyncKey,
  ShopifyThemeKey,
} from "state-trees/src/connections/shopify/keys";
import { NO_REQUIRED_SCOPE, ShopifyReadWriteScopes } from "state-trees/src/connections/shopify/scopes";
import type { ShopifyWebhookTopic } from "state-trees/src/connections/shopify/webhooks";
import type { ConnectionModelSpec } from "state-trees/src/connections/utils";
import { SeparateChildren } from "web/src/components/edit/shared/SeparateChildren";
import { TD, TH, TR, Table } from "web/src/components/markdown/Table";
import { Subdued } from "web/src/components/markdown/Typography";
import { DocLink, InlineCode } from "../../markdown-components";

const formatModelName = (name: string) => name.replace(/Shopify /, "");

/**
 * Get the metadata for the root metadata the given metadata belongs to
 * Example: RefundLineItem => Refund => Order */
const metaGroupKey = (metadata: ShopifyModelMetadata, apiVersion: ShopifyApiVersion) => {
  while (metadata.parentKey && metadata.parentKey != ShopifyShopKey) {
    const parentMetadata = shopifyModelMetadataForVersion(metadata.parentKey, apiVersion);
    if (!parentMetadata) {
      break;
    }
    metadata = parentMetadata;
  }
  return metadata.spec(shopifyModelSpecOptions(apiVersion))?.key;
};

const PublicDistributionMessage = (props: { name: string }) => {
  return (
    <>
      {props.name} (and other billing resources) are only available to OAuth apps created via the Shopify CLI or in the Shopify Partners
      dashboard that are marked for Public distribution. To successfully receive webhooks or sync this model, you must mark your app for
      Public distribution. Find more instructions in the{" "}
      <DocLink href="https://shopify.dev/apps/distribution/select-distribution-method">Shopify docs</DocLink>.
    </>
  );
};

const PlusStoreModelsOnlyMessage = (props: { name: string }) => {
  return (
    <>
      {props.name} and its related models are only available for apps installed on Shopify Partners Plus stores. To successfully sync and
      receive webhooks for these models, you must have access to a Shopify Partners Plus store. Access to these models also grant you access
      to special fields in Shopify Order and Shopify Draft Order models that are only available for Plus stores only. Find more information
      in the <DocLink href="https://help.shopify.com/en/manual/b2b/companies">Shopify docs</DocLink>.
    </>
  );
};

const Notes: { [key: string]: ReactNode } = {
  [ShopifyCheckoutKey]: (
    <>
      The <InlineCode>read_checkouts</InlineCode> scope will allow Gadget to process checkout related webhooks, but does not provide a
      mechanism to sync all existing checkouts. <br />
      <br />
      Adding the <InlineCode>read_orders</InlineCode> scope, while also including the Checkout model in your application, will result in all{" "}
      <DocLink href="https://shopify.dev/docs/admin-api/rest/reference/orders/abandoned-checkouts">Abandoned Checkouts</DocLink> that
      aren&apos;t handled via webhooks (failed delivery, checkouts that already exist) being imported during nightly or manual syncs.
    </>
  ),
  [ShopifyInventoryItemKey]: (
    <>
      Inventory Items are synced using the{" "}
      <DocLink href="https://shopify.dev/api/admin-rest/2022-01/resources/inventorylevel#[get]/admin/api/2022-01/inventory_levels.json">
        REST API endpont
      </DocLink>{" "}
      for Inventory Levels for each Location to get a list of inventory levels, and then using the{" "}
      <DocLink href="https://shopify.dev/api/admin-rest/2022-01/resources/inventoryitem#[get]/admin/api/2022-01/inventory_items.json?ids=808950810,39072856,457924702">
        REST API endpoint
      </DocLink>{" "}
      to get a list of inventory items for each level.
    </>
  ),
  [ShopifyInventoryLevelKey]: (
    <>
      Inventory Levels are synced using the{" "}
      <DocLink href="https://shopify.dev/api/admin-rest/2022-01/resources/location#[get]/admin/api/2022-01/locations/{location_id}/inventory_levels.json">
        REST API
      </DocLink>{" "}
      endpoint for Inventory Levels at a Location.
    </>
  ),
  [ShopifyCollectionKey]: <>Gadget uses the Collection model to represent both Custom Collections and Smart Collections from Shopify.</>,
  [ShopifyDisputeKey]: <>Dispute data is only available for Shopify merchants using Shopify Payments.</>,
  [ShopifyDomainKey]: (
    <>
      Shopify Domain delete and update webhooks are missing key data, so Gadget does an inline sync of the Domain REST API resource when
      these webhooks arrive to properly discover updates and deletes.
    </>
  ),
  [ShopifyAppSubscriptionKey]: <PublicDistributionMessage name="Shopify App Subscription" />,
  [ShopifyAppPurchaseOneTimeKey]: <PublicDistributionMessage name="Shopify App Purchase One Time" />,
  [ShopifyAppCreditKey]: <PublicDistributionMessage name="Shopify App Credits" />,
  [ShopifyCompanyKey]: <PlusStoreModelsOnlyMessage name="Shopify Company" />,
  [ShopifyFileKey]: (
    <>
      This model tracks files uploaded by the merchant in the Files section of the Shopify Admin. Shopify doesn&apos;t expose webhooks for
      the File resource, so files are only updated in Gadget on sync. Files can be accessed via the <InlineCode>read_files</InlineCode> or{" "}
      <InlineCode>read_themes</InlineCode> scopes.
    </>
  ),
  [ShopifyGiftCardKey]: (
    <>
      This model tracks issued gift cards for a Shopify store. Shopify only allows access to the <InlineCode>read_gift_cards</InlineCode>{" "}
      scope on Shopify Plus stores, and only for{" "}
      <DocLink href="https://help.shopify.com/en/manual/apps/app-types/custom-apps">Custom apps</DocLink> provisioned within the Shopify
      Admin.
      <br />
      <br />
      Shopify doesn&apos;t offer gift card webhooks, so gift card data is only refreshed on sync.
    </>
  ),
  [ShopifyGdprRequestKey]: (
    <>
      This model tracks incoming GDPR webhook requests from Shopify to delete merchant or customer data. These GDPR webhooks are required to
      be supported by Public applications for the Shopify app store, which you can read more about in the{" "}
      <DocLink href="https://shopify.dev/apps/webhooks/configuration/mandatory-webhooks">Shopify Docs</DocLink>. This model doesn&apos;t
      correspond to an API endpoint within Shopify.
    </>
  ),
  [ShopifyThemeKey]: (
    <>
      Gadget only syncs themes and assets that a store owns for each Shopify store, which excludes demo themes. Themes and assets for themes
      with <InlineCode>role: &quot;demo&quot;</InlineCode> in the Shopify API are not synced as API clients don&apos;t have permission to
      access individual asset values.
    </>
  ),
  [ShopifyAssetKey]: (
    <>
      Gadget syncs the <DocLink href="https://shopify.dev/api/admin-rest/2023-01/resources/theme">Theme</DocLink> and{" "}
      <DocLink href="https://shopify.dev/api/admin-rest/2023-01/resources/asset">Asset</DocLink> resources from Shopify using the REST API.
      Gadget does not sync the <InlineCode>value</InlineCode> column of the Asset resource. Syncing the <InlineCode>value</InlineCode> field
      can be done with a custom code effect, but Shopify requires one API call per asset to retrieve asset values, which often stresses
      Shopify API rate limits too much. Shopify Theme assets are often also quite large, including images and large JS files that are
      generally not important for applications to sync and store again.
      <br />
      Gadget recommends avoiding syncing the Asset and Theme model if possible, and instead making API calls directly to Shopify to work
      with assets using the{" "}
      <DocLink href="/guides/plugins/shopify#adding-code-effects">
        <InlineCode>connections.shopify.current</InlineCode>
      </DocLink>{" "}
      API client.
      <br />
      Gadget only syncs themes and assets that a store owns for each Shopify store, which excludes demo themes. Themes and assets for themes
      with <InlineCode>role: &quot;demo&quot;</InlineCode> in the Shopify API are not synced as API clients don&apos;t have permission to
      access individual asset values.
    </>
  ),
  [ShopifyPageKey]: (
    <>
      Shopify pages support Metafields but do not provide a REST or GraphQL mechanism for syncing them. Gadget does not support Metafields
      on Shopify Page models.
    </>
  ),
  [ShopifyArticleKey]: (
    <>
      Shopify blog posts support Metafields but do not provide a REST or GraphQL mechanism for syncing them. Gadget does not support
      Metafields on Shopify Article models.
    </>
  ),
  [ShopifyBlogKey]: (
    <>
      Shopify blogs support Metafields but do not provide a REST or GraphQL mechanism for syncing them. Gadget does not support Metafields
      on Shopify Blog models.
    </>
  ),
  [ShopifyProductImageKey]: (
    <>
      This model has been deprecated. Read more about{" "}
      <DocLink href="/guides/plugins/shopify#deprecated-shopifyproductimage-model">ProductImage deprecation</DocLink>.
    </>
  ),
};

const ModelDetailsRow = ({
  child,
  apiVersion,
  spec,
  contributingScopes,
  inheritWebhooks,
  webhooks,
  parentKey,
}: {
  apiVersion: ShopifyApiVersion;
  child?: boolean;
  spec: ConnectionModelSpec;
  contributingScopes: readonly string[];
  inheritWebhooks?: boolean;
  webhooks: Partial<Record<ShopifyWebhookTopic, any>>;
  parentKey: string | null;
}) => {
  const [_css, $theme] = useStyletron();
  const modelSpecOptions = shopifyModelSpecOptions(apiVersion);
  if (spec.key == ShopifySyncKey) return null;

  const scopes = contributingScopes.map((scope) => {
    if (ShopifyReadWriteScopes.includes(scope)) {
      return `read_${scope}`;
    } else {
      return scope;
    }
  });
  if (scopes.length == 0) {
    scopes.push(NO_REQUIRED_SCOPE);
  }

  let webhookTopics: ReactNode;
  if (inheritWebhooks) {
    const parentSpec = shopifyModelMetadataForVersion(parentKey ?? "", apiVersion)?.spec(modelSpecOptions);
    if (parentSpec) {
      webhookTopics = <Subdued>Sent within {formatModelName(parentSpec.name)} model</Subdued>;
    } else {
      // We should never reach this, but just in case we accidentally do, have something available.
      webhookTopics = <Subdued>Sent within parent model</Subdued>;
    }
  } else {
    const topicNames = uniq(Object.keys(webhooks));
    if (topicNames.length > 0) {
      webhookTopics = (
        <SeparateChildren>
          {topicNames.map((topic) => (
            <InlineCode key={topic}>{topic}</InlineCode>
          ))}
        </SeparateChildren>
      );
    } else {
      webhookTopics = <Subdued>synced only</Subdued>;
    }
  }

  const notes = Notes[spec.key];
  const firstRowStyle = { borderBottomWidth: notes ? "0px" : undefined, maxWidth: "inherit" };

  return (
    <>
      <TR key={spec.key}>
        <TD style={{ ...firstRowStyle, paddingLeft: child ? $theme.sizing.scale800 : undefined }}>{formatModelName(spec.name)}</TD>
        <TD style={firstRowStyle}>
          <SeparateChildren>
            {scopes.map((scope) => {
              if (scope == NO_REQUIRED_SCOPE) {
                return "Always available";
              } else {
                return <InlineCode key={scope}>{scope}</InlineCode>;
              }
            }) ?? ["Always available"]}
          </SeparateChildren>
        </TD>
        <TD style={firstRowStyle}>{webhookTopics}</TD>
      </TR>
      {notes && (
        <TR>
          <TD />
          <TD colSpan={2}>{notes}</TD>
        </TR>
      )}
    </>
  );
};

export const ShopifyModelDetailsTable = (props: { apiVersion: ShopifyApiVersion }) => {
  const groups: Record<string, { meta: ShopifyModelMetadata; children: ShopifyModelMetadata[] }> = {};
  const apiVersion = props.apiVersion;
  const modelSpecOptions = shopifyModelSpecOptions(apiVersion);

  for (const meta of eachShopifyModelMetadataForVersion(apiVersion)) {
    const groupKey = metaGroupKey(meta, apiVersion);

    if (!groupKey) {
      continue;
    }

    groups[groupKey] ??= {
      meta: null as any,
      children: [],
    };

    if (meta.spec(modelSpecOptions)?.key == groupKey) {
      groups[groupKey].meta = meta;
    } else {
      groups[groupKey].children.push(meta);
    }
  }

  return (
    <Table $style={{ overflowX: "scroll" }}>
      <TR>
        <TH>Model Name</TH>
        <TH>Required Scopes</TH>
        <TH>Webhook Topics</TH>
      </TR>
      {Object.values(groups).map((group) => {
        const spec = group.meta.spec(modelSpecOptions);
        return (
          spec && (
            <React.Fragment key={spec.key}>
              <ModelDetailsRow
                spec={spec}
                contributingScopes={group.meta.contributingScopes}
                webhooks={group.meta.webhooks}
                inheritWebhooks={group.meta.inheritWebhooks}
                parentKey={group.meta.parentKey}
                apiVersion={apiVersion}
              />
              {group.children.map((child) => {
                const childSpec = child.spec(modelSpecOptions);

                return (
                  childSpec && (
                    <ModelDetailsRow
                      key={childSpec.key}
                      spec={childSpec}
                      contributingScopes={child.contributingScopes}
                      webhooks={child.webhooks}
                      inheritWebhooks={child.inheritWebhooks}
                      parentKey={child.parentKey}
                      apiVersion={apiVersion}
                      child
                    />
                  )
                );
              })}
            </React.Fragment>
          )
        );
      })}
    </Table>
  );
};
